import React, { useMemo } from "react";
import { useRouteMatch } from "react-router";
import { InputLabel } from "@material-ui/core";
import { KDatePicker, KButton } from "@kyburz/kdesign-system";
import { useSelector } from "react-redux";
import useSafeSetState from "../../../../hooks/useSafeSetState";
import { getFsmEventsByDevice } from "../../../../apiCalls/config/devices";
import { formatDate, formatUtcDate } from "../../../../utils/timeUtils";
import { getTachoDownloads } from "../../../../apiCalls/admin/tacho";
import KInfiniteTable from "../../../../components/table/KInfiniteTable";
import { useStyles } from "./styles";

const today = new Date();
const tomorrow = new Date();
tomorrow.setDate(today.getDate() + 1);
const FsmHistory = () => {
  const match = useRouteMatch({
    path: "/config/devices/device/:imei/fsm_history",
    strict: true,
    sensitive: true,
  });
  const currentCustomer: string = useSelector(
    (state: any) => state?.customer?.currentCustomer?.shortname,
  );
  const {imei} = match?.params as any;
  const classes = useStyles();
  const [
    {startDate, endDate, startTime, endTime, events, loading, tachoDlData},
    setState,
  ] = useSafeSetState({
    startDate: today,
    endDate: tomorrow,
    startTime: "00:00",
    endTime: "00:00",
    events: [],
    loading: false,
    tachoDlData: {},
  });

  const onDateChange = (dates): void => {
    const [start, end] = dates;
    setState({startDate: start, endDate: end});
  };

  const handleTimeChange = (e): void => {
    setState({[e.target.name]: e.target.value});
  };

  const getDownloadNames = (download_ids) => {
    if (!download_ids.length) {
      return;
    }
    getTachoDownloads(currentCustomer, 1, download_ids.length, `_id:in:${download_ids.join(",")}`)
      .then(res => {
        const data = res?.data?.data?.data?.reduce((r, d) => {
          const id = d?._id;
          return ({
            ...r,
            [id]: d,
          });
        }, {...tachoDlData});
        setState({
          tachoDlData: data,
        })
      })
  }

  const handleGetHistory = () => {
    setState({loading: true});
    getFsmEventsByDevice(
      imei,
      startDate.toISOString().slice(0, 10),
      endDate.toISOString().slice(0, 10),
      startTime,
      endTime,
    ).then((res) => {
      if (res && res.data) {
        const dl_ids: string[] = [];
        const data = res.data.map((e) => {
          const dl_id = e?.data?.tco_tacho_data?.download?.id;
          if (dl_id) {
            dl_ids.push(dl_id);
          }
          return ({
            created_at: formatUtcDate(e?.created_at),
            event_name: e?.event_name,
            state_name: e?.state_name,
            state_description: e?.state_description,
            download_id: dl_id,
            dl_status: e?.data?.tco_tacho_data?.tacho_status?.status,
          })
        });
        setState({events: data, loading: false});

        getDownloadNames([...new Set(dl_ids)]);
      }
    });
  };

  const columns = useMemo(() => ([
    {
      Header: "Created",
      accessor: "created_at",
    }, {
      Header: "Event Name",
      accessor: "event_name",
    }, {
      Header: "State Name",
      accessor: "state_name",
    }, {
      Header: "State Description",
      accessor: "state_description",
    }, {
      Header: "DL Name",
      accessor: (row) => {
        return tachoDlData[row?.download_id]?.title || "";
      },
    }, {
      Header: "DL Created",
      accessor: (row) => {
        const created = tachoDlData[row?.download_id]?.created;
        return created ? formatDate(created) : "";
      },
    }, {
      Header: "DL Status",
      accessor: "dl_status",
    },
  ]), [events.length, tachoDlData])

  return (
    <div className={classes.root}>
      <div className={classes.controls}>
        <KDatePicker
          selected={startDate}
          onChange={onDateChange}
          selectsStart={true}
          label="Date Range"
          startDate={startDate}
          compact
          endDate={endDate}
        />
        <div className={classes.time}>
          <InputLabel classes={{root: classes.title}}>From (UTC)</InputLabel>
          <input
            type="time"
            id="startTime"
            name="startTime"
            value={startTime}
            onChange={handleTimeChange}
            className={classes.timeInput}
          />
        </div>
        <div className={classes.time}>
          <InputLabel classes={{root: classes.title}}>To (UTC)</InputLabel>
          <input
            type="time"
            id="endTime"
            name="endTime"
            value={endTime}
            onChange={handleTimeChange}
            className={classes.timeInput}
          />
        </div>
        <KButton
          children="Search History"
          onClick={handleGetHistory}
          loading={loading}
          disabled={loading}
          style={{marginLeft: 16}}
        />
      </div>
      <div className={classes.tableContainer}>
        {events.length > 0 ? <KInfiniteTable
          columns={columns}
          data={events}
          hasNextPage={false}
          isNextPageLoading={false}
          loading={loading}
          rootClassName={classes.tableRoot}
        /> : "No data for the selected time period"}
      </div>
    </div>
  );
};

export default FsmHistory;
